/**
 * ============================================================================
 * RevenueNew - COMPLETE REVENUE PAGE REBUILD
 * ============================================================================
 *
 * ⚠️  CRITICAL WARNINGS FOR FUTURE CLAUDE SESSIONS:
 *
 * 1. THIS REPLACES THE OLD 1390-LINE DISASTER
 *    - Clean composition of components
 *    - Uses TanStack Query (NO localStorage)
 *    - 100% type safe (NO 'any')
 *    - NO demo data injection
 *
 * 2. STATE MANAGEMENT
 *    - All data state in TanStack Query hooks
 *    - Simple filter and modal state
 *    - Separate sport filters for Revenue by Sport vs Peak Hours
 *    - No useEffect needed (hooks handle everything)
 *
 * 3. COMPONENT COMPOSITION
 *    - Each component receives data via props
 *    - No prop drilling (max 1 level deep)
 *    - Components are independent and reusable
 *    - Easy to test and maintain
 *
 * 4. ERROR HANDLING
 *    - Proper loading states with spinner
 *    - User-friendly error messages
 *    - Retry mechanism
 *    - NO alert() calls
 *
 * 5. READY FOR SUPER ADMIN
 *    - Components accept data via props
 *    - Can reuse 80% for aggregated views
 *    - Just need different hooks for multi-venue data
 *
 * ============================================================================
 */

import { CURRENCY_SYMBOL } from '../utils/currency';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmation } from '../contexts/ConfirmationContext';
import { AlertCircle, RefreshCw, Smartphone, UserPlus, Download, ChevronDown } from 'lucide-react';

// Hooks
import { useRevenueData } from '../hooks/useRevenueData';
import { usePayoutRequest } from '../hooks/usePayoutRequest';
// import { useRecentTransactions } from '../hooks/useRecentTransactions'; // Commented out - Phase 1 (manual bookings only)
import { usePeakHours } from '../hooks/usePeakHours';

// Components
import { RevenueHeader } from '../components/revenue/RevenueHeader';
import { RevenueStats } from '../components/revenue/RevenueStats';
import { RevenueTrendChart } from '../components/revenue/RevenueTrendChart';
import { RevenueBreakdownSection } from '../components/revenue/RevenueBreakdownSection';
import { PeakHoursSection } from '../components/revenue/PeakHoursSection';
import { RevenueCompositionChart } from '../components/revenue/RevenueCompositionChart';
import { PayoutInformationCard } from '../components/revenue/PayoutInformationCard';
import { BankAccountCard } from '../components/revenue/BankAccountCard';
import { RecentPayoutsCard } from '../components/revenue/RecentPayoutsCard';
// import { RecentTransactionsPreview } from '../components/revenue/RecentTransactionsPreview'; // Commented out - Phase 1 (manual bookings only)
// import { RevenueBySourceChart } from '../components/revenue/RevenueBySourceChart'; // Commented out - replaced by payment/source charts
import { PaymentMethodChart } from '../components/revenue/PaymentMethodChart';
import { BookingSourceChart } from '../components/revenue/BookingSourceChart';

// Modals
import { AvailableBookingsModal } from '../components/revenue/modals/AvailableBookingsModal';
import { HeldBookingsModal } from '../components/revenue/modals/HeldBookingsModal';
import { ReservedRefundsModal } from '../components/revenue/modals/ReservedRefundsModal';
import { PayoutResultModal } from '../components/revenue/modals/PayoutResultModal';

// Types
import type { PayoutRequestResult, RevenuePeriod } from '../types/revenue';

/**
 * Revenue page - production-ready rebuild
 */
export default function RevenueNew() {
  const { user } = useAuth();
  const { alert } = useConfirmation();
  const navigate = useNavigate();
  const venue = user?.venues?.[0];

  // ============================================================================
  // STATE
  // ============================================================================

  // Filter state
  const [selectedPeriod, setSelectedPeriod] = useState<RevenuePeriod>('daily');
  const [sportFilter, setSportFilter] = useState<number | null>(null);
  const [peakHoursSportFilter, setPeakHoursSportFilter] = useState<number | null>(null);
  const [selectedSource, setSelectedSource] = useState<'all' | 'spotts' | 'manual'>('all');

  // Modal state
  const [showAvailableModal, setShowAvailableModal] = useState(false);
  const [showHeldModal, setShowHeldModal] = useState(false);
  const [showReservedModal, setShowReservedModal] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutResult, setPayoutResult] = useState<PayoutRequestResult | null>(null);

  // Export dropdown state
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  // ============================================================================
  // DATA FETCHING (TanStack Query - NO localStorage!)
  // ============================================================================

  // Main earnings data with auto-refresh
  const {
    data: earningsData,
    isPending,
    error,
    refetch,
  } = useRevenueData(venue?.id, { source: selectedSource });

  // Peak hours with separate sport filter
  const { data: peakHours } = usePeakHours(venue?.id, peakHoursSportFilter);

  // Recent transactions - Commented out for Phase 1 (manual bookings only)
  // const { data: transactions } = useRecentTransactions(venue?.id);

  // Payout mutation
  const { mutate: requestPayout, isPending: requestingPayout } = usePayoutRequest(
    venue?.id,
    {
      onSuccess: (result) => {
        setPayoutResult(result);
        setShowPayoutModal(true);
      },
      onError: (error) => {
        setPayoutResult(error);
        setShowPayoutModal(true);
      },
    }
  );

  // ============================================================================
  // HANDLERS
  // ============================================================================

  /**
   * Export revenue report as CSV
   * @param section - Optional specific section to export, or 'all' for everything
   */
  type ExportSection = 'all' | 'summary' | 'revenue_by_source' | 'payment_methods' | 'booking_sources' | 'revenue_by_sport' | 'revenue_trend' | 'peak_hours';

  const handleExport = async (section: ExportSection = 'all') => {
    if (!earningsData) return;
    setShowExportDropdown(false);

    // Helper to properly escape CSV values (quote values with commas/quotes/newlines)
    const escapeCSV = (value: string | number | undefined | null): string => {
      if (value === undefined || value === null) return '""';
      const str = String(value);
      // If contains comma, quote, or newline, wrap in quotes and escape internal quotes
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return `"${str}"`;
    };

    // Format currency for display
    const formatAmount = (amount: number) => `${CURRENCY_SYMBOL}${amount.toLocaleString('en-NG')}`;

    // Build CSV content as an array of lines
    const lines: string[] = [];

    // ===== SECTION 1: SUMMARY =====
    if (section === 'all' || section === 'summary') {
      lines.push('REVENUE SUMMARY');
      lines.push(`Generated,${escapeCSV(new Date().toLocaleString())}`);
      lines.push(`Source Filter,${escapeCSV(getSourceLabel())}`);
      lines.push('');
      lines.push('Metric,Value');
      lines.push(`Total Revenue,${escapeCSV(formatAmount(earningsData.total_revenue))}`);
      lines.push(`Vendor Earnings (${earningsData.vendor_share_percentage}%),${escapeCSV(formatAmount(earningsData.vendor_earnings))}`);
      lines.push(`Platform Commission (${earningsData.platform_share_percentage}%),${escapeCSV(formatAmount(earningsData.platform_commission))}`);
      lines.push(`Total Refunds (${earningsData.refund_count} refunds),${escapeCSV(formatAmount(earningsData.total_refunds))}`);
      if (selectedSource !== 'manual') {
        lines.push(`Outstanding Debt,${escapeCSV(formatAmount(earningsData.outstanding_debt || 0))}`);
      }
      lines.push(`Net Earnings,${escapeCSV(formatAmount(selectedSource === 'manual' ? earningsData.vendor_earnings : earningsData.net_earnings))}`);
      if (section === 'all') lines.push('');
    }

    // ===== SECTION 2: REVENUE BY SOURCE =====
    if ((section === 'all' || section === 'revenue_by_source') && earningsData.revenue_by_source) {
      lines.push('REVENUE BY SOURCE');
      lines.push('Source,Revenue,Bookings');
      const { spotts, manual, recurring, memberships } = earningsData.revenue_by_source;
      if (spotts && (spotts.revenue > 0 || spotts.bookings > 0)) {
        lines.push(`Spotts App,${escapeCSV(formatAmount(spotts.revenue))},${spotts.bookings}`);
      }
      if (manual && (manual.revenue > 0 || manual.bookings > 0)) {
        lines.push(`Manual/Walk-in,${escapeCSV(formatAmount(manual.revenue))},${manual.bookings}`);
      }
      if (recurring && (recurring.revenue > 0 || recurring.bookings > 0)) {
        lines.push(`Recurring,${escapeCSV(formatAmount(recurring.revenue))},${recurring.bookings}`);
      }
      if (memberships && (memberships.revenue > 0 || memberships.bookings > 0)) {
        lines.push(`Memberships,${escapeCSV(formatAmount(memberships.revenue))},${memberships.bookings}`);
      }
      if (section === 'all') lines.push('');
    }

    // ===== SECTION 3: PAYMENT METHOD BREAKDOWN =====
    if ((section === 'all' || section === 'payment_methods') && earningsData.payment_method_breakdown && earningsData.payment_method_breakdown.length > 0) {
      lines.push('PAYMENT METHOD BREAKDOWN');
      lines.push('Method,Revenue,Bookings,Percentage');
      earningsData.payment_method_breakdown.forEach((pm) => {
        const methodName = pm.method.charAt(0).toUpperCase() + pm.method.slice(1);
        lines.push(`${escapeCSV(methodName)},${escapeCSV(formatAmount(pm.revenue))},${pm.bookings},${pm.percentage}%`);
      });
      if (section === 'all') lines.push('');
    }

    // ===== SECTION 4: BOOKING SOURCE BREAKDOWN =====
    if ((section === 'all' || section === 'booking_sources') && earningsData.booking_source_breakdown && earningsData.booking_source_breakdown.length > 0) {
      lines.push('BOOKING SOURCE BREAKDOWN');
      lines.push('Source,Revenue,Bookings,Percentage');
      earningsData.booking_source_breakdown.forEach((bs) => {
        const sourceName = bs.source.charAt(0).toUpperCase() + bs.source.slice(1);
        lines.push(`${escapeCSV(sourceName)},${escapeCSV(formatAmount(bs.revenue))},${bs.bookings},${bs.percentage}%`);
      });
      if (section === 'all') lines.push('');
    }

    // ===== SECTION 5: REVENUE BY SPORT =====
    if ((section === 'all' || section === 'revenue_by_sport') && earningsData.revenue_by_sport && earningsData.revenue_by_sport.length > 0) {
      lines.push('REVENUE BY SPORT');
      lines.push('Sport,Revenue,Bookings,Percentage');
      earningsData.revenue_by_sport.forEach((sport: any) => {
        lines.push(`${escapeCSV(sport.sport_name)},${escapeCSV(formatAmount(sport.revenue))},${sport.bookings},${sport.percentage}%`);
      });
      if (section === 'all') lines.push('');
    }

    // ===== SECTION 6: REVENUE TREND =====
    if (section === 'all' || section === 'revenue_trend') {
      const trendData =
        selectedPeriod === 'daily'
          ? earningsData.revenue_trend?.daily
          : selectedPeriod === 'weekly'
          ? earningsData.revenue_trend?.weekly
          : earningsData.revenue_trend?.monthly;

      if (trendData && trendData.length > 0) {
        const dateHeader =
          selectedPeriod === 'daily' ? 'Date' : selectedPeriod === 'weekly' ? 'Week' : 'Month';

        lines.push(`REVENUE TREND (${selectedPeriod.toUpperCase()})`);
        lines.push(`${dateHeader},Gross Revenue,Commission,Net Revenue,Bookings`);

        trendData.forEach((item: Record<string, unknown>) => {
          const period = (item.date || item.week || item.month || 'N/A') as string;
          const gross = Number(item.revenue || item.gross_revenue || 0);
          const commission = Number(item.commission || 0);
          const net = Number(item.net || item.net_earnings || 0);
          const bookings = Number(item.bookings_count ?? item.bookings ?? item.booking_count ?? item.total_bookings ?? 0);

          lines.push(`${escapeCSV(period)},${escapeCSV(formatAmount(gross))},${escapeCSV(formatAmount(commission))},${escapeCSV(formatAmount(net))},${bookings}`);
        });

        // Add totals row
        const totalGross = trendData.reduce((sum: number, item: Record<string, unknown>) =>
          sum + Number(item.revenue || item.gross_revenue || 0), 0);
        const totalCommission = trendData.reduce((sum: number, item: Record<string, unknown>) =>
          sum + Number(item.commission || 0), 0);
        const totalNet = trendData.reduce((sum: number, item: Record<string, unknown>) =>
          sum + Number(item.net || item.net_earnings || 0), 0);
        const totalBookings = trendData.reduce((sum: number, item: Record<string, unknown>) =>
          sum + Number(item.bookings_count ?? item.bookings ?? item.booking_count ?? item.total_bookings ?? 0), 0);

        lines.push('');
        lines.push(`TOTAL,${escapeCSV(formatAmount(totalGross))},${escapeCSV(formatAmount(totalCommission))},${escapeCSV(formatAmount(totalNet))},${totalBookings}`);
      }
    }

    // ===== SECTION 7: PEAK HOURS =====
    if (section === 'all' || section === 'peak_hours') {
      const peakHoursData = peakHours || earningsData.peak_hours;
      if (peakHoursData && peakHoursData.length > 0) {
        if (section === 'all') lines.push('');
        lines.push('PEAK HOURS');
        lines.push('Time,Revenue,Bookings,Percentage');
        peakHoursData.forEach((ph: any) => {
          lines.push(`${escapeCSV(ph.time)},${escapeCSV(formatAmount(ph.revenue))},${ph.bookings},${ph.percentage}%`);
        });
      }
    }

    // Check if we have any data to export
    if (lines.length <= 1) {
      await alert({
        title: 'No Data',
        message: 'No data available to export for this section.',
        variant: 'info',
      });
      return;
    }

    // Generate filename based on section
    const sectionName = section === 'all' ? 'full_report' : section;
    const csvContent = lines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `revenue_${sectionName}_${selectedSource}_${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ============================================================================
  // HELPER: Get source label
  // ============================================================================

  const getSourceLabel = () => {
    switch (selectedSource) {
      case 'spotts':
        return 'Spotts App only';
      case 'manual':
        return 'Manual bookings only';
      default:
        return 'All sources';
    }
  };

  // ============================================================================
  // LOADING STATE (Only show on first load, not on tab switches)
  // ============================================================================

  if (isPending) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading revenue data...</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // ERROR STATE
  // ============================================================================

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Revenue</h2>
        <p className="text-gray-600 mb-4">
          {error instanceof Error ? error.message : 'An error occurred'}
        </p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
        >
          Retry
        </button>
      </div>
    );
  }

  // ============================================================================
  // NO VENUE
  // ============================================================================

  if (!venue || !earningsData) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle className="w-12 h-12 text-gray-400 mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">No Venue Found</h2>
        <p className="text-gray-600">Please ensure you have a venue associated with your account.</p>
      </div>
    );
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {/* Header with Compact Source Selector and Export */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <RevenueHeader />

        {/* Source Selector + Export Dropdown */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Compact Source Selector */}
          <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 p-1.5 shadow-sm">
            <button
              onClick={() => setSelectedSource('all')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-1.5 ${
                selectedSource === 'all'
                  ? 'bg-gray-900 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setSelectedSource('spotts')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-1.5 ${
                selectedSource === 'spotts'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Smartphone className="w-3 h-3" />
              Spotts
            </button>
            <button
              onClick={() => setSelectedSource('manual')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-1.5 ${
                selectedSource === 'manual'
                  ? 'bg-green-600 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <UserPlus className="w-3 h-3" />
              Manual
            </button>
          </div>

          {/* Export Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowExportDropdown(!showExportDropdown)}
              className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors text-xs shadow-sm"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Export</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showExportDropdown ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {showExportDropdown && (
              <>
                {/* Backdrop to close dropdown */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowExportDropdown(false)}
                />
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl border border-gray-200 shadow-lg z-20 overflow-hidden">
                  <div className="py-1">
                    <button
                      onClick={() => handleExport('all')}
                      className="w-full px-4 py-2.5 text-left text-sm font-medium text-gray-900 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Download className="w-4 h-4 text-green-600" />
                      Export All Data
                    </button>
                    <div className="border-t border-gray-100 my-1" />
                    <p className="px-4 py-1.5 text-xs text-gray-400 font-medium uppercase">Individual Sections</p>
                    <button
                      onClick={() => handleExport('summary')}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Revenue Summary
                    </button>
                    <button
                      onClick={() => handleExport('revenue_by_source')}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Revenue by Source
                    </button>
                    <button
                      onClick={() => handleExport('payment_methods')}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Payment Methods
                    </button>
                    <button
                      onClick={() => handleExport('booking_sources')}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Booking Sources
                    </button>
                    <button
                      onClick={() => handleExport('revenue_by_sport')}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Revenue by Sport
                    </button>
                    <button
                      onClick={() => handleExport('revenue_trend')}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Revenue Trend ({selectedPeriod})
                    </button>
                    <button
                      onClick={() => handleExport('peak_hours')}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Peak Hours
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Source Label */}
      {selectedSource !== 'all' && (
        <div className={`text-sm font-medium px-3 py-1.5 rounded-lg inline-flex items-center gap-2 ${
          selectedSource === 'spotts'
            ? 'bg-blue-50 text-blue-700'
            : 'bg-green-50 text-green-700'
        }`}>
          {selectedSource === 'spotts' ? (
            <Smartphone className="w-3.5 h-3.5" />
          ) : (
            <UserPlus className="w-3.5 h-3.5" />
          )}
          Showing {getSourceLabel()}
        </div>
      )}

      {/* Stats Cards */}
      {/* Note: Debt only applies to Spotts bookings (gateway fees), not manual bookings */}
      <RevenueStats
        totalRevenue={earningsData.total_revenue}
        vendorEarnings={earningsData.vendor_earnings}
        vendorSharePercentage={earningsData.vendor_share_percentage}
        platformCommission={earningsData.platform_commission}
        platformSharePercentage={earningsData.platform_share_percentage}
        totalRefunds={earningsData.total_refunds}
        refundCount={earningsData.refund_count}
        heldBalance={earningsData.payout?.held_balance || 0}
        heldBookingsCount={earningsData.payout?.held_bookings_count || 0}
        outstandingDebt={selectedSource === 'manual' ? 0 : (earningsData.outstanding_debt || 0)}
        netEarnings={selectedSource === 'manual' ? earningsData.vendor_earnings : earningsData.net_earnings}
        sourceFilter={selectedSource}
        onViewHeld={() => setShowHeldModal(true)}
      />

      {/* Revenue Trend Chart - Dynamic based on source */}
      <RevenueTrendChart
        dailyData={earningsData.revenue_trend?.daily || []}
        weeklyData={earningsData.revenue_trend?.weekly || []}
        monthlyData={earningsData.revenue_trend?.monthly || []}
        selectedPeriod={selectedPeriod}
        onPeriodChange={setSelectedPeriod}
        onExport={handleExport}
        source={selectedSource}
      />

      {/* Revenue Breakdown & Peak Hours - SEPARATE FILTERS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RevenueBreakdownSection
          sportsRevenue={earningsData.revenue_by_sport || []}
          selectedSportId={sportFilter}
          onSportChange={setSportFilter}
        />

        <PeakHoursSection
          peakHours={peakHours || earningsData.peak_hours || []}
          availableSports={earningsData.revenue_by_sport || []}
          selectedSportId={peakHoursSportFilter}
          onSportChange={setPeakHoursSportFilter}
        />
      </div>

      {/* Revenue Composition - Bookings vs Memberships */}
      {earningsData.revenue_by_source && (
        <RevenueCompositionChart revenueBySource={earningsData.revenue_by_source} />
      )}

      {/* Payout Information */}
      {earningsData.payout && (
        <PayoutInformationCard
          payoutInfo={earningsData.payout}
          onRequestPayout={() => requestPayout()}
          requesting={requestingPayout}
          onViewAvailable={() => setShowAvailableModal(true)}
          onViewHeld={() => setShowHeldModal(true)}
          onViewReserved={() => setShowReservedModal(true)}
        />
      )}

      {/* Bank Account & Recent Payouts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BankAccountCard
          bankDetails={earningsData.user}
          vendorSharePercentage={earningsData.vendor_share_percentage}
          payoutDay={earningsData.payout?.payout_day || 'Monday'}
          minimumThreshold={earningsData.payout?.minimum_threshold || 5000}
        />

        <RecentPayoutsCard
          payoutHistory={earningsData.payout?.history || []}
          onViewAll={() => navigate('/payouts')}
        />
      </div>

      {/* Revenue by Source Chart - Commented out, replaced by payment/source charts */}
      {/* {earningsData.revenue_by_source && (
        <RevenueBySourceChart
          spotts={earningsData.revenue_by_source.spotts}
          manual={earningsData.revenue_by_source.manual}
          totalRevenue={earningsData.total_revenue}
        />
      )} */}

      {/* Manual Booking Insights - Payment Method & Booking Source */}
      {(selectedSource === 'manual' || selectedSource === 'all') && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {earningsData.payment_method_breakdown && earningsData.payment_method_breakdown.length > 0 && (
            <PaymentMethodChart data={earningsData.payment_method_breakdown} />
          )}
          {earningsData.booking_source_breakdown && earningsData.booking_source_breakdown.length > 0 && (
            <BookingSourceChart data={earningsData.booking_source_breakdown} />
          )}
        </div>
      )}

      {/* Recent Transactions - Commented out for Phase 1 (manual bookings only)
      <RecentTransactionsPreview
        transactions={transactions || []}
        onViewAll={() => navigate('/transactions')}
      />
      */}

      {/* Modals */}
      {showAvailableModal && (
        <AvailableBookingsModal venueId={venue.id} onClose={() => setShowAvailableModal(false)} />
      )}

      {showHeldModal && (
        <HeldBookingsModal venueId={venue.id} onClose={() => setShowHeldModal(false)} />
      )}

      {showReservedModal && (
        <ReservedRefundsModal venueId={venue.id} onClose={() => setShowReservedModal(false)} />
      )}

      {showPayoutModal && payoutResult && (
        <PayoutResultModal
          isOpen={showPayoutModal}
          result={payoutResult}
          onClose={() => {
            setShowPayoutModal(false);
            setPayoutResult(null);
          }}
        />
      )}
    </div>
  );
}
